/**
 * Lieflat Charts Design Tokens & Geometry Utilities
 * Based on .agents/skills/lieflat-charts (Mono, Porcelain, Palm, Wire)
 */

export const LIEFLAT_TOKENS = {
  // Line & stroke weights
  stroke: {
    hairline: 0.8,
    axis: 1.0,
    curve: 2.0,
    hero: 2.6,
    dashGuide: "3 3",
    dashFine: "2 2",
    dashMilestone: "4 3",
  },
  // Radii
  radius: {
    card: 16,
    pill: 9999,
    barCapsule: 3,
    bead: 3.5,
    halo: 6.5,
  },
  // Typography hierarchy
  typography: {
    titleWeight: 700,
    metricWeight: 800,
    axisWeight: 600,
    metaWeight: 500,
    monoFont: '"JetBrains Mono", "Cascadia Code", monospace',
  },
  // Animation easings
  easing: {
    quarticOut: "cubic-bezier(0.25, 1, 0.5, 1)",
    standard: "cubic-bezier(0.4, 0, 0.2, 1)",
  },
};

/**
 * Nice Scale calculator for axis divisions with round numbers
 */
export function getLieflatNiceScale(
  rawMax: number,
  targetCount = 4,
  options: { isCurrency?: boolean; isInteger?: boolean; isPercentage?: boolean; minMax?: number } = {}
): { ticks: number[]; maxAxis: number; step: number } {
  const minFloor = options.minMax ?? (options.isPercentage ? 0.1 : options.isCurrency ? 10 : 1);
  const safeMax = Math.max(minFloor, rawMax * 1.12);
  const roughStep = safeMax / targetCount;
  const exponent = Math.floor(Math.log10(Math.max(0.001, roughStep)));
  const fraction = roughStep / Math.pow(10, exponent);

  let niceFraction = 1;
  if (fraction <= 1.2) niceFraction = 1;
  else if (fraction <= 2.2) niceFraction = 2;
  else if (fraction <= 3.5) niceFraction = 2.5;
  else if (fraction <= 7.5) niceFraction = 5;
  else niceFraction = 10;

  let step = niceFraction * Math.pow(10, exponent);
  if (options.isInteger) step = Math.max(1, Math.round(step));

  const count = Math.max(2, Math.ceil(safeMax / step));
  const maxAxis = count * step;
  const ticks: number[] = [];
  for (let i = 0; i <= count; i++) {
    ticks.push(Number((i * step).toFixed(options.isPercentage ? 3 : 2)));
  }

  return { ticks, maxAxis, step };
}

/**
 * Natural cubic Bezier curve path generator for silky smooth trajectories
 */
export function getLieflatSmoothPath(pts: Array<{ x: number; y: number }>): string {
  if (pts.length === 0) return "";
  if (pts.length === 1) return `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
  if (pts.length === 2) return `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)} L ${pts[1].x.toFixed(1)} ${pts[1].y.toFixed(1)}`;

  let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];

    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;

    d += ` C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
  }
  return d;
}

/**
 * Natural cubic Bezier area path generator with zero-ground clamp
 */
export function getLieflatSmoothAreaPath(pts: Array<{ x: number; y: number }>, bottomY: number): string {
  if (pts.length <= 1) return "";
  const curve = getLieflatSmoothPath(pts);
  const firstX = pts[0].x.toFixed(1);
  const lastX = pts[pts.length - 1].x.toFixed(1);
  return `${curve} L ${lastX} ${bottomY.toFixed(1)} L ${firstX} ${bottomY.toFixed(1)} Z`;
}

/**
 * Short currency formatter for chart axis labels (e.g. €1.2k, $500)
 */
export function formatLieflatCurrencyShort(amount: number, symbol = "€"): string {
  if (amount >= 1000000) {
    return `${symbol}${(amount / 1000000).toFixed(1)}M`;
  }
  if (amount >= 1000) {
    return `${symbol}${(amount / 1000).toFixed(amount % 1000 === 0 ? 0 : 1)}k`;
  }
  return `${symbol}${amount.toLocaleString()}`;
}

/**
 * Formatter for percentage rates (e.g. 15%, 20%, 12.5%)
 */
export function formatLieflatRate(rate: number): string {
  const pct = rate * 100;
  if (Math.abs(pct - Math.round(pct)) < 0.01) {
    return `${Math.round(pct)}%`;
  }
  return `${pct.toFixed(1)}%`;
}

/**
 * Synchronized rate scale calculator for secondary Y-axis in dual-axis charts.
 * Ensures the rate axis has the exact same intervalCount as the primary axis,
 * aligning every grid line with a clean, round percentage tick and preventing
 * curve/label misalignment.
 */
export function getSynchronizedRateScale(
  rawMaxRate: number,
  intervalCount: number
): { ticks: number[]; maxAxis: number; step: number } {
  const safeCount = Math.max(1, intervalCount);
  const minFloor = 0.10;
  // 5% headroom so points at max rate don't clip the top boundary
  const safeMax = Math.max(minFloor, rawMaxRate * 1.05);
  const roughStep = safeMax / safeCount;

  // Nice human-friendly percentage steps (multiples of 1%, 2%, 2.5%, 5%, 10%, 15%, 20%, 25%, 50%)
  const candidateSteps = [
    0.01, 0.02, 0.025, 0.05, 0.10, 0.15, 0.20, 0.25, 0.50
  ];
  let step = candidateSteps.find((s) => s * safeCount >= safeMax);
  if (!step) {
    step = Math.ceil(roughStep * 10) / 10;
  }

  const maxAxis = Number((safeCount * step).toFixed(4));
  const ticks: number[] = [];
  for (let i = 0; i <= safeCount; i++) {
    ticks.push(Number((i * step).toFixed(4)));
  }

  return { ticks, maxAxis, step };
}

